import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";

const UserAssetsPDFTemplate = ({ userData, officeWiseAssets, userOffices }) => {
  const styles = StyleSheet.create({
    page: {
      backgroundColor: "#ffffff",
      fontSize: "10px",
      fontFamily: "Helvetica",
      padding: "20px",
    },
    header: {
      backgroundColor: "#2980b9",
      color: "white",
      padding: "15px",
      textAlign: "center",
      marginBottom: "20px",
    },
    headerTitle: {
      fontSize: "18px",
      fontWeight: "bold",
      marginBottom: "5px",
    },
    headerSubtitle: {
      fontSize: "12px",
      marginBottom: "5px",
    },
    employeeInfo: {
      backgroundColor: "#f8f9fa",
      padding: "15px",
      marginBottom: "20px",
      border: "1px solid #dee2e6",
      borderRadius: "5px",
    },
    employeeInfoTitle: {
      fontSize: "14px",
      fontWeight: "bold",
      marginBottom: "10px",
      color: "#495057",
    },
    employeeInfoRow: {
      flexDirection: "row",
      marginBottom: "5px",
    },
    employeeInfoLabel: {
      width: "80px",
      fontWeight: "bold",
      color: "#6c757d",
    },
    employeeInfoValue: {
      flex: 1,
      color: "#212529",
    },
    officeSection: {
      marginBottom: "30px",
      border: "2px solid #2980b9",
      borderRadius: "8px",
      overflow: "hidden",
      pageBreak: "avoid",
    },
    officeHeader: {
      backgroundColor: "#2980b9",
      color: "white",
      padding: "15px",
      borderBottom: "2px solid #2980b9",
    },
    officeTitle: {
      fontSize: "16px",
      fontWeight: "bold",
      color: "white",
      marginBottom: "5px",
    },
    officeTotal: {
      fontSize: "12px",
      color: "white",
      opacity: 0.9,
    },
    storeSection: {
      marginBottom: "20px",
      border: "1px solid #dee2e6",
      borderRadius: "5px",
      overflow: "hidden",
      pageBreak: "avoid",
    },
    storeHeader: {
      backgroundColor: "#6c757d",
      color: "white",
      padding: "10px 15px",
      borderBottom: "1px solid #dee2e6",
    },
    storeTitle: {
      fontSize: "14px",
      fontWeight: "bold",
      color: "white",
    },
    storeTotal: {
      fontSize: "12px",
      color: "white",
      opacity: 0.9,
      marginTop: "3px",
    },
    categorySection: {
      marginBottom: "15px",
      pageBreak: "avoid",
    },
    categoryHeader: {
      backgroundColor: "#f8f9fa",
      padding: "8px 15px",
      borderBottom: "1px solid #dee2e6",
    },
    categoryTitle: {
      fontSize: "12px",
      fontWeight: "bold",
      color: "#495057",
    },
    table: {
      display: "table",
      width: "100%",
      borderStyle: "solid",
      borderWidth: "1px",
      borderColor: "#dee2e6",
      pageBreak: "auto",
    },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: "1px",
      borderBottomColor: "#dee2e6",
      minHeight: "25px",
      alignItems: "center",
    },
    tableHeader: {
      backgroundColor: "#495057",
      color: "white",
      fontWeight: "bold",
      pageBreak: "avoid",
    },
    tableCell: {
      padding: "5px 8px",
      fontSize: "8px",
      borderRightWidth: "1px",
      borderRightColor: "#dee2e6",
      textAlign: "center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    tableCellLast: {
      borderRightWidth: "0px",
    },
    tableCellSlNo: {
      width: "5%",
    },
    tableCellAssetName: {
      width: "12%",
    },
    tableCellOEMTag: {
      width: "16%",
      textAlign: "left",
      justifyContent: "flex-start",
      flexWrap: "wrap",
    },
    tableCellOEMBrand: {
      width: "10%",
    },
    tableCellModel: {
      width: "8%",
    },
    tableCellSpecification: {
      width: "28%",
      textAlign: "left",
      justifyContent: "flex-start",
    },
    tableCellWarrantyDate: {
      width: "12%",
    },
    tableCellIssueDate: {
      width: "9%",
    },
    tableBody: {
      pageBreak: "auto",
    },
    summarySection: {
      backgroundColor: "#28a745",
      color: "white",
      padding: "15px",
      marginTop: "20px",
      borderRadius: "5px",
    },
    summaryTitle: {
      fontSize: "14px",
      fontWeight: "bold",
      marginBottom: "10px",
    },
    summaryRow: {
      flexDirection: "row",
      marginBottom: "5px",
    },
    summaryLabel: {
      width: "120px",
      fontWeight: "bold",
    },
    summaryValue: {
      flex: 1,
    },
    summarySubRow: {
      flexDirection: "row",
      marginBottom: "3px",
      marginLeft: "20px",
    },
    summarySubLabel: {
      width: "100px",
      fontSize: "10px",
    },
    summarySubValue: {
      flex: 1,
      fontSize: "10px",
    },
    footer: {
      position: "absolute",
      bottom: "20px",
      left: "20px",
      right: "20px",
      textAlign: "center",
      fontSize: "8px",
      color: "#6c757d",
      borderTop: "1px solid #dee2e6",
      paddingTop: "10px",
    },
    pageNumber: {
      position: "absolute",
      bottom: "10px",
      right: "20px",
      fontSize: "8px",
      color: "#6c757d",
    },
  });

  // Helper function to format specification
  const formatSpecification = (spec) => {
    if (!spec || typeof spec !== 'object') return 'N/A';
    return Object.entries(spec)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  // Helper function to format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format("DD-MM-YYYY");
  };

  // Calculate total assets
  const totalAssets = Object.values(officeWiseAssets).reduce((officeSum, stores) => {
    return officeSum + Object.values(stores).reduce((storeSum, storeData) => {
      return storeSum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
    }, 0);
  }, 0);

  // Get office names with main office first, then additional offices sorted alphabetically
  const officeNames = Object.keys(officeWiseAssets).sort((a, b) => {
    const mainOffice = userData?.office?.mainCharge;
    
    // Main office comes first
    if (a === mainOffice) return -1;
    if (b === mainOffice) return 1;
    
    // Then sort additional offices alphabetically
    return a.localeCompare(b);
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>USER ASSET REPORT</Text>
          <Text style={styles.headerSubtitle}>Generated by Asset Management System</Text>
        </View>

        {/* Employee Information */}
        <View style={styles.employeeInfo}>
          <Text style={styles.employeeInfoTitle}>Employee Details</Text>
          <View style={styles.employeeInfoRow}>
            <Text style={styles.employeeInfoLabel}>Name:</Text>
            <Text style={styles.employeeInfoValue}>
              {userData?.name?.firstName ? 
                `${userData.name.firstName} ${userData.name.middleName || ''} ${userData.name.lastName || ''}`.trim() : 
                userData?.email || 'N/A'
              }
            </Text>
          </View>
          <View style={styles.employeeInfoRow}>
            <Text style={styles.employeeInfoLabel}>Main Office:</Text>
            <Text style={styles.employeeInfoValue}>{userData?.office?.mainCharge || 'N/A'}</Text>
          </View>
          <View style={styles.employeeInfoRow}>
            <Text style={styles.employeeInfoLabel}>All Offices:</Text>
            <Text style={styles.employeeInfoValue}>{userOffices?.join(', ') || 'N/A'}</Text>
          </View>
          <View style={styles.employeeInfoRow}>
            <Text style={styles.employeeInfoLabel}>Generated:</Text>
            <Text style={styles.employeeInfoValue}>{moment().format('DD-MM-YYYY HH:mm')}</Text>
          </View>
        </View>

        {/* Office-wise Assets */}
        {officeNames.map((officeName, officeIndex) => {
          const stores = officeWiseAssets[officeName];
          const storeNames = Object.keys(stores).sort();
          const officeTotal = storeNames.reduce((sum, storeName) => {
            const storeData = stores[storeName];
            return sum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
          }, 0);

          if (officeTotal === 0) return null;

          return (
            <View key={officeIndex} style={styles.officeSection}>
              {/* Office Header */}
              <View style={styles.officeHeader}>
                <Text style={styles.officeTitle}>Office: {officeName}</Text>
                <Text style={styles.officeTotal}>Total Assets: {officeTotal}</Text>
              </View>

              {/* Store-wise Assets within Office */}
              {storeNames.map((storeName, storeIndex) => {
                const storeData = stores[storeName];
                const storeTotal = storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                
                if (storeTotal === 0) return null;

                return (
                  <View key={storeIndex} style={styles.storeSection}>
                    {/* Store Header */}
                    <View style={styles.storeHeader}>
                      <Text style={styles.storeTitle}>Store: {storeName}</Text>
                      <Text style={styles.storeTotal}>Total Assets: {storeTotal}</Text>
                    </View>

                    {/* Individual Assets */}
                    {storeData.individual.length > 0 && (
                      <View style={styles.categorySection}>
                        <View style={styles.categoryHeader}>
                          <Text style={styles.categoryTitle}>Individual Assets: {storeData.individual.length}</Text>
                        </View>
                        <View style={styles.table}>
                          <View style={[styles.tableRow, styles.tableHeader]}>
                            <Text style={[styles.tableCell, styles.tableCellSlNo]}>Sl.No.</Text>
                            <Text style={[styles.tableCell, styles.tableCellAssetName]}>Asset Name</Text>
                            <Text style={[styles.tableCell, styles.tableCellOEMTag]}>OEM Tag</Text>
                            <Text style={[styles.tableCell, styles.tableCellOEMBrand]}>OEM/Brand</Text>
                            <Text style={[styles.tableCell, styles.tableCellModel]}>Model</Text>
                            <Text style={[styles.tableCell, styles.tableCellSpecification]}>Specification</Text>
                            <Text style={[styles.tableCell, styles.tableCellWarrantyDate]}>Warranty Date</Text>
                            <Text style={[styles.tableCell, styles.tableCellIssueDate, styles.tableCellLast]}>Issue Date</Text>
                          </View>
                          <View style={styles.tableBody}>
                            {storeData.individual.map((asset, index) => (
                              <View key={index} style={styles.tableRow}>
                                <Text style={[styles.tableCell, styles.tableCellSlNo]}>{index + 1}</Text>
                                <Text style={[styles.tableCell, styles.tableCellAssetName]}>{asset.name || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellOEMTag]}>{asset.oem_tag || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellOEMBrand]}>{asset.oem || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellModel]}>{asset.model_no || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellSpecification]}>{formatSpecification(asset.specification)}</Text>
                                <Text style={[styles.tableCell, styles.tableCellWarrantyDate]}>{formatDate(asset.warrantyexp_date)}</Text>
                                <Text style={[styles.tableCell, styles.tableCellIssueDate, styles.tableCellLast]}>{formatDate(asset.last_trans_date)}</Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      </View>
                    )}

                    {/* Office Assets */}
                    {storeData.office.length > 0 && (
                      <View style={styles.categorySection}>
                        <View style={styles.categoryHeader}>
                          <Text style={styles.categoryTitle}>Office Assets: {storeData.office.length}</Text>
                        </View>
                        <View style={styles.table}>
                          <View style={[styles.tableRow, styles.tableHeader]}>
                            <Text style={[styles.tableCell, styles.tableCellSlNo]}>Sl.No.</Text>
                            <Text style={[styles.tableCell, styles.tableCellAssetName]}>Asset Name</Text>
                            <Text style={[styles.tableCell, styles.tableCellOEMTag]}>OEM Tag</Text>
                            <Text style={[styles.tableCell, styles.tableCellOEMBrand]}>OEM/Brand</Text>
                            <Text style={[styles.tableCell, styles.tableCellModel]}>Model</Text>
                            <Text style={[styles.tableCell, styles.tableCellSpecification]}>Specification</Text>
                            <Text style={[styles.tableCell, styles.tableCellWarrantyDate]}>Warranty Date</Text>
                            <Text style={[styles.tableCell, styles.tableCellIssueDate, styles.tableCellLast]}>Issue Date</Text>
                          </View>
                          <View style={styles.tableBody}>
                            {storeData.office.map((asset, index) => (
                              <View key={index} style={styles.tableRow}>
                                <Text style={[styles.tableCell, styles.tableCellSlNo]}>{index + 1}</Text>
                                <Text style={[styles.tableCell, styles.tableCellAssetName]}>{asset.name || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellOEMTag]}>{asset.oem_tag || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellOEMBrand]}>{asset.oem || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellModel]}>{asset.model_no || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellSpecification]}>{formatSpecification(asset.specification)}</Text>
                                <Text style={[styles.tableCell, styles.tableCellWarrantyDate]}>{formatDate(asset.warrantyexp_date)}</Text>
                                <Text style={[styles.tableCell, styles.tableCellIssueDate, styles.tableCellLast]}>{formatDate(asset.last_trans_date)}</Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      </View>
                    )}

                    {/* BO Assets */}
                    {storeData.bo.length > 0 && (
                      <View style={styles.categorySection}>
                        <View style={styles.categoryHeader}>
                          <Text style={styles.categoryTitle}>BO Assets: {storeData.bo.length}</Text>
                        </View>
                        <View style={styles.table}>
                          <View style={[styles.tableRow, styles.tableHeader]}>
                            <Text style={[styles.tableCell, styles.tableCellSlNo]}>Sl.No.</Text>
                            <Text style={[styles.tableCell, styles.tableCellAssetName]}>Asset Name</Text>
                            <Text style={[styles.tableCell, styles.tableCellOEMTag]}>OEM Tag</Text>
                            <Text style={[styles.tableCell, styles.tableCellOEMBrand]}>OEM/Brand</Text>
                            <Text style={[styles.tableCell, styles.tableCellModel]}>Model</Text>
                            <Text style={[styles.tableCell, styles.tableCellSpecification]}>Specification</Text>
                            <Text style={[styles.tableCell, styles.tableCellWarrantyDate]}>Warranty Date</Text>
                            <Text style={[styles.tableCell, styles.tableCellIssueDate, styles.tableCellLast]}>Issue Date</Text>
                          </View>
                          <View style={styles.tableBody}>
                            {storeData.bo.map((asset, index) => (
                              <View key={index} style={styles.tableRow}>
                                <Text style={[styles.tableCell, styles.tableCellSlNo]}>{index + 1}</Text>
                                <Text style={[styles.tableCell, styles.tableCellAssetName]}>{asset.name || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellOEMTag]}>{asset.oem_tag || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellOEMBrand]}>{asset.oem || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellModel]}>{asset.model_no || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellSpecification]}>{formatSpecification(asset.specification)}</Text>
                                <Text style={[styles.tableCell, styles.tableCellWarrantyDate]}>{formatDate(asset.warrantyexp_date)}</Text>
                                <Text style={[styles.tableCell, styles.tableCellIssueDate, styles.tableCellLast]}>{formatDate(asset.last_trans_date)}</Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      </View>
                    )}

                    {/* Store Assets */}
                    {storeData.store.length > 0 && (
                      <View style={styles.categorySection}>
                        <View style={styles.categoryHeader}>
                          <Text style={styles.categoryTitle}>Store Assets: {storeData.store.length}</Text>
                        </View>
                        <View style={styles.table}>
                          <View style={[styles.tableRow, styles.tableHeader]}>
                            <Text style={[styles.tableCell, styles.tableCellSlNo]}>Sl.No.</Text>
                            <Text style={[styles.tableCell, styles.tableCellAssetName]}>Asset Name</Text>
                            <Text style={[styles.tableCell, styles.tableCellOEMTag]}>OEM Tag</Text>
                            <Text style={[styles.tableCell, styles.tableCellOEMBrand]}>OEM/Brand</Text>
                            <Text style={[styles.tableCell, styles.tableCellModel]}>Model</Text>
                            <Text style={[styles.tableCell, styles.tableCellSpecification]}>Specification</Text>
                            <Text style={[styles.tableCell, styles.tableCellWarrantyDate]}>Warranty Date</Text>
                            <Text style={[styles.tableCell, styles.tableCellIssueDate, styles.tableCellLast]}>Issue Date</Text>
                          </View>
                          <View style={styles.tableBody}>
                            {storeData.store.map((asset, index) => (
                              <View key={index} style={styles.tableRow}>
                                <Text style={[styles.tableCell, styles.tableCellSlNo]}>{index + 1}</Text>
                                <Text style={[styles.tableCell, styles.tableCellAssetName]}>{asset.name || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellOEMTag]}>{asset.oem_tag || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellOEMBrand]}>{asset.oem || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellModel]}>{asset.model_no || 'N/A'}</Text>
                                <Text style={[styles.tableCell, styles.tableCellSpecification]}>{formatSpecification(asset.specification)}</Text>
                                <Text style={[styles.tableCell, styles.tableCellWarrantyDate]}>{formatDate(asset.warrantyexp_date)}</Text>
                                <Text style={[styles.tableCell, styles.tableCellIssueDate, styles.tableCellLast]}>{formatDate(asset.last_trans_date)}</Text>
                              </View>
                            ))}
                          </View>
                        </View>
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          );
        })}

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <Text style={styles.summaryTitle}>ASSET SUMMARY</Text>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Assets:</Text>
            <Text style={styles.summaryValue}>{totalAssets}</Text>
          </View>
          {officeNames.map((officeName, officeIndex) => {
            const stores = officeWiseAssets[officeName];
            const storeNames = Object.keys(stores).sort();
            const officeTotal = storeNames.reduce((sum, storeName) => {
              const storeData = stores[storeName];
              return sum + storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
            }, 0);
            
            if (officeTotal === 0) return null;
            
            return (
              <View key={officeIndex}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>• {officeName}:</Text>
                  <Text style={styles.summaryValue}>{officeTotal} assets</Text>
                </View>
                {storeNames.map((storeName, storeIndex) => {
                  const storeData = stores[storeName];
                  const storeTotal = storeData.individual.length + storeData.office.length + storeData.bo.length + storeData.store.length;
                  
                  if (storeTotal === 0) return null;
                  
                  return (
                    <View key={storeIndex} style={styles.summarySubRow}>
                      <Text style={styles.summarySubLabel}>  - Store: {storeName}:</Text>
                      <Text style={styles.summarySubValue}>{storeTotal} assets</Text>
                    </View>
                  );
                })}
              </View>
            );
          })}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Generated by Asset Management System</Text>
        </View>

        {/* Page Number */}
        <Text style={styles.pageNumber}>Page 1</Text>
      </Page>
    </Document>
  );
};

export default UserAssetsPDFTemplate;
